import { useRef } from "preact/hooks";
import type { ComponentChildren, VNode } from "preact";
import { Carousel, CarouselRef } from "@core/components/Elements/Carousel/Carousel";
import { Slide } from "@core/components/Elements/Carousel/Slide";

import AbolitionistLogo from "../assets/images/abolitionist-logo.svg";
import OnboardingBg from "../assets/images/onboading-bg.webp";
import HeroBg from "../assets/images/hero-bg.webp";

interface OnboardingProps {
  onComplete: () => void;
  onContinue?: () => void;
  onClose?: () => void;
}

interface SlideShellProps {
  children: ComponentChildren;
  actionLabel: string;
  onAction: () => void;
}

// Each slide owns its own button so the button swipes with the slide content,
// instead of staying put while the text/image scrolls behind it.
function SlideShell({ children, actionLabel, onAction }: SlideShellProps): VNode {
  return (
    <div className="h-full w-full flex flex-col items-center justify-between px-[14%] pt-[14%] pb-[72px] pointer-events-none">
      <div className="w-full flex flex-col items-center">
        {children}
      </div>
      <button
        type="button"
        onClick={onAction}
        className="pointer-events-auto w-full max-w-[260px] h-[54px] flex items-center justify-center bg-transparent border-2 border-white text-white font-display font-semibold text-[18px] uppercase hover:bg-white/10 active:bg-white/10 transition-colors"
        style={{ letterSpacing: "2px" }}
      >
        {actionLabel}
      </button>
    </div>
  );
}

export function Onboarding({ onComplete, onContinue }: OnboardingProps): VNode {
  const carouselRef = useRef<CarouselRef>(null);
  const next = () => {
    onContinue?.();
    carouselRef.current?.scrollNext();
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center pointer-events-auto px-5 select-none">
      {/* Backdrop dims the scene around the modal. */}
      <div className="absolute inset-0 bg-black/60" />

      {/* Modal — the bg PNG has the scene treatment and border baked in (800×1200, aspect 2/3). */}
      <div
        className="relative w-full max-w-[400px] aspect-[2/3] bg-cover bg-center"
        style={{
          backgroundImage: `url('${OnboardingBg}')`,
          maxHeight: "calc(100vh - 120px)",
        }}
      >
        <div className="absolute inset-0">
          <Carousel
            ref={carouselRef}
            dotColor="#FFFFFF"
            dotsOffsetBottom={40}
            options={{ loop: false }}
          >
            <Slide>
              <SlideShell actionLabel="Continue" onAction={next}>
                <img
                  src={AbolitionistLogo}
                  alt="Friends of Abolitionist Place"
                  className="w-[130px] h-auto"
                  draggable={false}
                />
                <p className="text-white text-[16px] font-medium leading-[1.3] mt-8 text-left w-full">
                  Step into the story of Brooklyn's abolitionist movement through immersive AR.
                  <br />
                  <br />
                  Discover the people, places, and moments that shaped the fight against slavery.
                </p>
              </SlideShell>
            </Slide>
            <Slide>
              <SlideShell actionLabel="Begin" onAction={onComplete}>
                <div className="w-full max-w-[210px] aspect-[230/129] border-2 border-white overflow-hidden">
                  <img
                    src={HeroBg}
                    alt=""
                    aria-hidden="true"
                    className="w-full h-full object-cover"
                    draggable={false}
                  />
                </div>
                <p className="text-white text-[16px] font-bold leading-[1.3] mt-8 w-full text-left">
                  Instructions:
                </p>
                <p className="text-white text-[16px] font-medium leading-[1.3] mt-2 text-left w-full">
                  Point your camera into the space ahead to position the panel.
                  <br />
                  <br />
                  Align it with the street for a more immersive view, or place it anywhere to explore.
                </p>
              </SlideShell>
            </Slide>
          </Carousel>
        </div>
      </div>
    </div>
  );
}
